import React, { useState } from 'react';
import { api } from './App';
import { Wave, Calendar, Books, ArrowRight, ArrowLeft, Upload, Pencil } from './Icons';

const PROVIDERS = ['BARBRI', 'BPP', 'Kaplan', 'Revise SQE', 'Epimeleia', 'ULaw', 'Other'];

// SQE1 exam cohorts — dates will be confirmed by SRA; update in Settings if different
const COHORTS = [
  { id: 'jul26-both',  label: 'July 2026',      sub: 'Both FLK1 & FLK2',          flk1: '2026-07-07', flk2: '2026-07-08' },
  { id: 'jan27-both',  label: 'January 2027',   sub: 'Both FLK1 & FLK2',          flk1: '2027-01-13', flk2: '2027-01-14' },
  { id: 'jul26-flk1',  label: 'Resitting FLK1', sub: 'FLK1 only, July 2026',      flk1: '2026-07-07', flk2: '' },
  { id: 'jul26-flk2',  label: 'Resitting FLK2', sub: 'FLK2 only, July 2026',      flk1: '',           flk2: '2026-07-08' },
  { id: 'manual',      label: 'Enter manually', sub: 'I know my specific dates',   flk1: null,         flk2: null },
];

const RATING_OPTIONS = [
  { label: 'Low',    value: 0.25 },
  { label: 'Some',   value: 0.55 },
  { label: 'Good',   value: 0.75 },
  { label: 'Strong', value: 0.90 },
];

export default function Onboarding({ userName, onComplete }) {
  const [step, setStep] = useState(1);
  const [name, setName]             = useState(userName || '');
  const [flk1Date, setFlk1Date]     = useState('');
  const [flk2Date, setFlk2Date]     = useState('');
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [showManualDates, setShowManualDates] = useState(false);
  const [provider, setProvider]     = useState('');
  const [providerOther, setProviderOther] = useState('');
  const [saving, setSaving]         = useState(false);
  const [mcqTarget, setMcqTarget] = useState('40');
  const [prepTarget, setPrepTarget] = useState('0.70');

  // Phase 2 — path selection & rating
  const [onboardPath, setOnboardPath] = useState(null); // null | 'revising'
  const [generalTopics, setGeneralTopics] = useState([]);
  const [ratings, setRatings] = useState({});           // key: `${flk}||${general_topic}` → number
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [savingRatings, setSavingRatings] = useState(false);

  // Save steps 1-4 settings then advance to step 5
  const saveAndAdvance = async (skipProvider) => {
    setSaving(true);
    const effectiveProvider = skipProvider ? '' : (provider === 'Other' ? providerOther : provider);
    const accountUpdate = { name };
    if (flk1Date) accountUpdate.exam_date_flk1 = flk1Date;
    if (flk2Date) accountUpdate.exam_date_flk2 = flk2Date;
    const settingsUpdate = {};
    if (flk1Date) settingsUpdate.flk1_exam_date = flk1Date;
    if (flk2Date) settingsUpdate.flk2_exam_date = flk2Date;
    if (effectiveProvider) settingsUpdate.material_provider = effectiveProvider;
    if (mcqTarget) settingsUpdate.mcq_daily_target = mcqTarget;
    settingsUpdate.target_confidence = prepTarget;
    await Promise.all([
      api('/api/account', { method: 'PATCH', body: JSON.stringify(accountUpdate) }),
      api('/api/settings', { method: 'POST', body: JSON.stringify(settingsUpdate) }),
    ]);
    setSaving(false);
    setStep(5);
  };

  // Complete onboarding — dest controls which tab the app opens on
  const finishOnboarding = async (dest = 'dashboard') => {
    await api('/api/settings', { method: 'POST', body: JSON.stringify({
      onboarding_complete: '1',
      tour_shown: '0',
    }) });
    onComplete(name, dest);
  };

  // Load general topics for the rating screen
  const loadGeneralTopics = async () => {
    setLoadingTopics(true);
    try {
      const topics = await api('/api/topics');
      const seen = new Set();
      const gt = [];
      for (const t of topics) {
        if (!t.general_topic) continue;
        const key = `${t.flk_number}||${t.general_topic}`;
        if (!seen.has(key)) {
          seen.add(key);
          gt.push({
            main_topic: t.main_topic || t.topic,
            general_topic: t.general_topic,
            flk_number: t.flk_number,
          });
        }
      }
      setGeneralTopics(gt);
    } catch (e) {
      console.error('Failed to load topics', e);
    } finally {
      setLoadingTopics(false);
    }
  };

  // Submit ratings via bulk-set-confidence, then finish
  const submitRatings = async () => {
    setSavingRatings(true);
    try {
      const ratingsList = Object.entries(ratings)
        .filter(([, v]) => v !== null)
        .map(([key, confidence]) => {
          const sepIdx = key.indexOf('||');
          return {
            flk_number: parseInt(key.slice(0, sepIdx), 10),
            general_topic: key.slice(sepIdx + 2),
            confidence,
          };
        });
      if (ratingsList.length > 0) {
        await api('/api/topics/bulk-set-confidence', {
          method: 'POST',
          body: JSON.stringify({ ratings: ratingsList }),
        });
      }
      await finishOnboarding();
    } finally {
      setSavingRatings(false);
    }
  };

  const dots = (
    <div className="ob-dots">
      {[1, 2, 3, 4, 5].map(n => (
        <span key={n} className={`ob-dot ${step === n ? 'active' : step > n ? 'done' : ''}`} />
      ))}
    </div>
  );

  // ── Step 1: Name ──
  if (step === 1) return (
    <div className="ob-fullpage">
      <div className="ob-fullpage-inner">
        {dots}
        <div className="ob-icon"><Wave size={32} /></div>
        <h2 className="ob-title">Welcome to SQETrack</h2>
        <p className="ob-text">
          Let's take a minute to get you set up. We'll configure your exam dates, study materials, and get your dashboard working.
        </p>
        <label className="ob-label">What should we call you?</label>
        <input className="ob-input" value={name} onChange={e => setName(e.target.value)}
          placeholder="Your first name" autoFocus
          onKeyDown={e => e.key === 'Enter' && name.trim() && setStep(2)} />
        <button className="ob-btn" onClick={() => setStep(2)} disabled={!name.trim()}>
          Continue <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );

  // ── Step 2: Exam cohort ──
  if (step === 2) return (
    <div className="ob-fullpage">
      <div className="ob-fullpage-inner">
        {dots}
        <div className="ob-icon"><Calendar size={32} /></div>
        <h2 className="ob-title">Which exam sitting are you preparing for?</h2>
        <p className="ob-text">
          This powers your countdown, urgency ranking, and planner. You can update dates any time in Settings.
        </p>
        <div className="ob-cohort-grid">
          {COHORTS.map(c => (
            <button
              key={c.id}
              className={`ob-cohort-btn ${selectedCohort === c.id ? 'selected' : ''}`}
              onClick={() => {
                setSelectedCohort(c.id);
                if (c.id === 'manual') {
                  setShowManualDates(true);
                } else {
                  setShowManualDates(false);
                  if (c.flk1 !== null) setFlk1Date(c.flk1);
                  if (c.flk2 !== null) setFlk2Date(c.flk2);
                }
              }}>
              <span className="ob-cohort-label">{c.label}</span>
              <span className="ob-cohort-sub">{c.sub}</span>
            </button>
          ))}
        </div>
        {showManualDates && (
          <div className="ob-date-row" style={{ marginTop: 16 }}>
            <div className="ob-date-col">
              <label className="ob-label">FLK1 date</label>
              <input className="ob-input" type="date" value={flk1Date} onChange={e => setFlk1Date(e.target.value)} />
            </div>
            <div className="ob-date-col">
              <label className="ob-label">FLK2 date</label>
              <input className="ob-input" type="date" value={flk2Date} onChange={e => setFlk2Date(e.target.value)} />
            </div>
          </div>
        )}
        <div className="ob-btn-row" style={{ marginTop: 20 }}>
          <button className="ob-back" onClick={() => setStep(1)}><ArrowLeft size={14} /> Back</button>
          <button className="ob-btn" onClick={() => setStep(3)}>Continue <ArrowRight size={14} /></button>
        </div>
        <button className="ob-skip" onClick={() => setStep(3)}>Skip for now</button>
      </div>
    </div>
  );

  // ── Step 3: Study materials ──
  if (step === 3) return (
    <div className="ob-fullpage">
      <div className="ob-fullpage-inner">
        {dots}
        <div className="ob-icon"><Books size={32} /></div>
        <h2 className="ob-title">Which study materials are you using?</h2>
        <p className="ob-text">
          This helps us give the most relevant guidance when importing MCQ results.
        </p>
        <div className="ob-provider-grid">
          {PROVIDERS.map(p => (
            <button key={p} className={`ob-provider-btn ${provider === p ? 'selected' : ''}`}
              onClick={() => setProvider(p)}>{p}</button>
          ))}
        </div>
        {provider === 'Other' && (
          <input className="ob-input" value={providerOther} onChange={e => setProviderOther(e.target.value)}
            placeholder="Which provider?" style={{ marginTop: 10 }} autoFocus />
        )}
        <div className="ob-btn-row">
          <button className="ob-back" onClick={() => setStep(2)}><ArrowLeft size={14} /> Back</button>
          <button className="ob-btn" onClick={() => setStep(4)}>Continue <ArrowRight size={14} /></button>
        </div>
        <button className="ob-skip" onClick={() => setStep(4)}>Skip for now</button>
      </div>
    </div>
  );

  // ── Step 4: Targets ──
  if (step === 4) return (
    <div className="ob-fullpage">
      <div className="ob-fullpage-inner">
        {dots}
        <div className="ob-icon">🎯</div>
        <h2 className="ob-title">Set your study targets</h2>
        <p className="ob-text">
          These help the planner and dashboard work effectively. You can always change them in Settings.
        </p>

        <div style={{ width: '100%', marginBottom: 20 }}>
          <label className="ob-label">Daily MCQ target</label>
          <p style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 8px' }}>How many practice questions do you want to aim for each day?</p>
          <input className="ob-input" type="number" min="1" max="999" value={mcqTarget}
            onChange={e => setMcqTarget(e.target.value)} style={{ width: 100 }} />
        </div>

        <div style={{ width: '100%', marginBottom: 20 }}>
          <label className="ob-label">Preparation target</label>
          <p style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 8px' }}>How confident do you want to be on each topic before the exam? You can change this later in Settings.</p>
          <div className="ob-provider-grid">
            {[
              { value: '0.60', label: 'Solid', desc: '60%' },
              { value: '0.70', label: 'Well prepared', desc: '70%' },
              { value: '0.80', label: 'Very prepared', desc: '80%' },
            ].map(opt => (
              <button key={opt.value}
                className={`ob-provider-btn ${prepTarget === opt.value ? 'selected' : ''}`}
                onClick={() => setPrepTarget(opt.value)}>
                {opt.label} ({opt.desc})
              </button>
            ))}
          </div>
        </div>

        <div className="ob-btn-row">
          <button className="ob-back" onClick={() => setStep(3)}><ArrowLeft size={14} /> Back</button>
          <button className="ob-btn" onClick={() => saveAndAdvance(false)} disabled={saving}>
            {saving ? 'Setting up…' : <>Continue <ArrowRight size={14} /></>}
          </button>
        </div>
        <button className="ob-skip" onClick={() => saveAndAdvance(true)}>Skip for now</button>
      </div>
    </div>
  );

  // ── Step 5: Path selection ──
  if (step === 5 && !onboardPath) {
    return (
      <div className="ob-fullpage">
        <div className="ob-fullpage-inner">
          {dots}
          <h2 className="ob-title">How would you like to get started?</h2>
          <p className="ob-text">
            Choose the option that best describes where you are with your revision.
          </p>
          <div className="ob-import-cards-v2">

            <button className="ob-import-card-v2 ob-import-card-v2--skip" onClick={() => finishOnboarding()}>
              <div className="ob-import-card-v2-icon ob-import-card-v2-icon--grey">
                <Pencil size={20} />
              </div>
              <div className="ob-import-card-v2-body">
                <div className="ob-import-card-v2-title">I'm starting fresh</div>
                <div className="ob-import-card-v2-text">New to the syllabus or just getting started. All topics begin at zero.</div>
              </div>
              <ArrowRight size={16} className="ob-import-card-v2-arrow ob-import-card-v2-arrow--grey" />
            </button>

            <button className="ob-import-card-v2" onClick={() => { setOnboardPath('revising'); loadGeneralTopics(); }}>
              <div className="ob-import-card-v2-icon ob-import-card-v2-icon--blue">
                <Books size={20} />
              </div>
              <div className="ob-import-card-v2-body">
                <div className="ob-import-card-v2-title">I've been revising</div>
                <div className="ob-import-card-v2-text">Rate your confidence across the main topic areas. Takes about 5 minutes.</div>
              </div>
              <ArrowRight size={16} className="ob-import-card-v2-arrow" />
            </button>

            <button className="ob-import-card-v2" onClick={() => finishOnboarding('mcqresults')}>
              <div className="ob-import-card-v2-icon ob-import-card-v2-icon--blue">
                <Upload size={20} />
              </div>
              <div className="ob-import-card-v2-body">
                <div className="ob-import-card-v2-title">I have BARBRI or Law Drills results</div>
                <div className="ob-import-card-v2-text">Import your practice question scores. We map them to your topics automatically.</div>
              </div>
              <ArrowRight size={16} className="ob-import-card-v2-arrow" />
            </button>

          </div>
          <button className="ob-back ob-back--center" style={{ marginTop: 16 }} onClick={() => setStep(4)}>
            <ArrowLeft size={14} /> Back
          </button>
        </div>
      </div>
    );
  }

  // ── Step 5b: Confidence rating grid (I've been revising) ──
  if (step === 5 && onboardPath === 'revising') {
    // Group: FLK label → main_topic → [generalTopic entries]
    const byFlk = {};
    for (const gt of generalTopics) {
      const flkLabel = gt.flk_number === 2 ? 'FLK 2' : 'FLK 1';
      if (!byFlk[flkLabel]) byFlk[flkLabel] = {};
      const mt = gt.main_topic || 'Other';
      if (!byFlk[flkLabel][mt]) byFlk[flkLabel][mt] = [];
      byFlk[flkLabel][mt].push(gt);
    }

    const ratedCount = Object.values(ratings).filter(v => v !== null).length;

    return (
      <div className="ob-fullpage">
        <div className="ob-fullpage-inner ob-fullpage-inner--wide">
          {dots}
          <h2 className="ob-title">Rate your current confidence</h2>
          <p className="ob-text">
            For each topic area, pick how confident you feel right now. Leave blank for topics you haven't covered yet.
          </p>

          {loadingTopics ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--muted)' }}>Loading topics…</div>
          ) : (
            <div className="ob-rating-scroll">
              {Object.entries(byFlk).map(([flkLabel, subjects]) => (
                <div key={flkLabel} className="ob-rating-module">
                  <div className="ob-rating-module-label">{flkLabel}</div>
                  {Object.entries(subjects).map(([mainTopic, gts]) => (
                    <div key={mainTopic} className="ob-rating-subject">
                      <div className="ob-rating-subject-name">{mainTopic}</div>
                      {gts.map(gt => {
                        const key = `${gt.flk_number}||${gt.general_topic}`;
                        const selected = ratings[key] ?? null;
                        return (
                          <div key={key} className="ob-rating-row">
                            <span className="ob-rating-topic-name">{gt.general_topic}</span>
                            <div className="ob-rating-btns">
                              {RATING_OPTIONS.map(opt => (
                                <button
                                  key={opt.label}
                                  className={`ob-rating-btn ${selected === opt.value ? 'ob-rating-btn--selected' : ''}`}
                                  data-level={opt.label.toLowerCase()}
                                  onClick={() => setRatings(r => ({
                                    ...r,
                                    [key]: selected === opt.value ? null : opt.value,
                                  }))}>
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          <div className="ob-btn-row" style={{ marginTop: 20 }}>
            <button className="ob-back" onClick={() => setOnboardPath(null)}>
              <ArrowLeft size={14} /> Back
            </button>
            <button className="ob-btn" onClick={submitRatings} disabled={savingRatings || loadingTopics}>
              {savingRatings ? 'Saving…' : (
                <>{ratedCount > 0 ? `Save ${ratedCount} rating${ratedCount !== 1 ? 's' : ''}` : 'Continue'} <ArrowRight size={14} /></>
              )}
            </button>
          </div>
          <button className="ob-skip" onClick={() => finishOnboarding()}>Skip and start blank</button>
        </div>
      </div>
    );
  }

  return null;
}
